"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { clsx } from 'clsx';
import { withSlotScopeClasses } from '../../helpers/scoped-slots.mjs';
import { forwardRef, Children } from 'react';
import { tv } from 'tailwind-variants';
import { Tooltip } from '../Tooltip/index.mjs';

const dataBuilderTitleRecipe = tv({
  slots: withSlotScopeClasses("data-builder-title", {
    root: "flex flex-row items-center justify-between gap-2 w-full max-w-full",
    "left-side": "flex flex-row items-center flex-1",
    "right-side": "flex flex-row items-center gap-1 h-full",
    title: "truncate flex-1 max-w-fit whitespace-nowrap font-medium",
    "title-wrap": "whitespace-normal"
  })
});
const Title = forwardRef(
  ({
    children,
    required,
    hasValue,
    hasError,
    hasNameConflict,
    leftSlot,
    typeSelectorSlot,
    rightSlot,
    childrenWrap = false,
    ...props
  }, ref) => {
    const styles = dataBuilderTitleRecipe();
    const childrenAsText = Children.toArray(children).reduce((acc, el) => {
      return acc + (typeof el === "string" ? el : "");
    }, "");
    const showRequiredDot = required && !hasError && !hasValue;
    const hasRightSlot = rightSlot || hasError || showRequiredDot;
    return /* @__PURE__ */ jsxs("div", { className: styles.root(), ...props, ref, children: [
      /* @__PURE__ */ jsxs("div", { className: styles["left-side"](), children: [
        leftSlot && /* @__PURE__ */ jsx("div", { children: leftSlot }),
        /* @__PURE__ */ jsx("span", { className: clsx(styles.title(), childrenWrap && styles["title-wrap"]()), title: childrenAsText, children }),
        rightSlot && /* @__PURE__ */ jsx(Fragment, { children: typeSelectorSlot })
      ] }),
      hasRightSlot && /* @__PURE__ */ jsxs("div", { className: styles["right-side"](), children: [
        rightSlot && /* @__PURE__ */ jsx(Fragment, { children: rightSlot }),
        hasError && /* @__PURE__ */ jsx(Dot, { reason: "Field has error" }),
        hasNameConflict && /* @__PURE__ */ jsx(Dot, { reason: "Current field name is already used" }),
        showRequiredDot && /* @__PURE__ */ jsx(Dot, { reason: "Field is required" })
      ] })
    ] });
  }
);
Title.displayName = "Title";
const dotRecipe = tv({
  slots: withSlotScopeClasses("data-builder-title-dot", {
    root: "w-4 flex justify-center items-center",
    dot: "shrink-0 grow-0 w-2 h-2 bg-tomato-10 rounded-full inline-block cursor-help"
  })
});
const Dot = forwardRef(({ reason, className, ...rest }, ref) => {
  const styles = dotRecipe();
  return /* @__PURE__ */ jsx(Tooltip, { tooltip: reason, delayToShow: 0, usePortal: true, children: /* @__PURE__ */ jsxs("div", { className: styles.root({ className }), children: [
    /* @__PURE__ */ jsx("span", { className: styles.dot(), ...rest, ref }),
    /* @__PURE__ */ jsx("span", { className: "sr-only", children: reason })
  ] }) });
});

export { Dot, Title };
